import * as Tooltip from "@radix-ui/react-tooltip";
import {
  ChevronRight,
  DatabaseBackup,
  LayoutDashboard,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Search,
  ShieldCheck,
  Ticket,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { forwardRef, useState, type AnchorHTMLAttributes, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useMatch, useResolvedPath } from "react-router";

import { useAuth } from "@/app/auth/context";
import { StudioSignature } from "@/components/brand/StudioSignature";
import { VaultMark } from "@/components/brand/VaultMark";
import { IconButton } from "@/components/ui/IconButton";
import { cn } from "@/lib/cn";

import { QueueTree } from "./QueueTree";

interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  end?: boolean;
  permission: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard, end: true, permission: "dashboard.view" },
  { to: "/tickets", labelKey: "nav.tickets", icon: Ticket, permission: "archive.browse" },
  { to: "/search", labelKey: "nav.search", icon: Search, permission: "search.fulltext" },
];

const ADMIN_ITEMS: NavItem[] = [
  { to: "/admin/imports", labelKey: "nav.imports", icon: DatabaseBackup, permission: "admin.import" },
  { to: "/admin/users", labelKey: "nav.users", icon: Users, permission: "admin.users" },
  { to: "/admin/roles", labelKey: "nav.roles", icon: ShieldCheck, permission: "admin.roles" },
  { to: "/admin/audit", labelKey: "nav.audit", icon: ScrollText, permission: "admin.audit" },
];

interface SidebarLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
  end?: boolean;
  rail: boolean;
  onNavigate: () => void;
}

/**
 * Navigation link with a plain string className: Radix `asChild` triggers merge class names as strings,
 * so NavLink's function form cannot be used inside a tooltip trigger.
 */
const SidebarLink = forwardRef<HTMLAnchorElement, SidebarLinkProps>(function SidebarLink(
  { to, end = false, rail, onNavigate, onClick, className, ...props },
  ref,
) {
  const resolved = useResolvedPath(to);
  const isActive = useMatch({ path: resolved.pathname, end }) !== null;
  return (
    <NavLink
      ref={ref}
      to={to}
      end={end}
      onClick={(event) => {
        onClick?.(event);
        onNavigate();
      }}
      className={cn(
        "relative flex h-10 items-center gap-3 rounded-control px-3 text-sm text-ink-muted",
        "transition-colors hover:bg-sunken hover:text-ink",
        rail && "md:justify-center md:px-0",
        isActive &&
          "bg-primary-soft font-medium text-primary hover:bg-primary-soft hover:text-primary " +
            "before:absolute before:inset-y-2 before:-left-3 before:w-[3px] before:rounded-r-full before:bg-accent",
        className,
      )}
      {...props}
    />
  );
});

function RailTooltip({ label, enabled, children }: { label: string; enabled: boolean; children: ReactNode }) {
  if (!enabled) return children;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={10}
          className="z-50 rounded-control bg-ink px-2.5 py-1.5 text-xs font-medium text-surface shadow-pop"
        >
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

const QUEUES_OPEN_KEY = "tv.sidebar.queuesOpen";

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // not remembered
  }
}

interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggleCollapsed: () => void;
  onCloseMobile: () => void;
}

export function Sidebar({ collapsed, mobileOpen, onToggleCollapsed, onCloseMobile }: SidebarProps) {
  const { t } = useTranslation();
  const { can } = useAuth();
  // On small screens the sidebar is a drawer and always shows labels.
  const rail = collapsed && !mobileOpen;
  const adminItems = ADMIN_ITEMS.filter((item) => can(item.permission));
  // The queue tree stays folded until opened; the choice is remembered on this device.
  const [queuesOpen, setQueuesOpen] = useState(() => readFlag(QUEUES_OPEN_KEY));
  const toggleQueues = () => {
    setQueuesOpen(!queuesOpen);
    writeFlag(QUEUES_OPEN_KEY, !queuesOpen);
  };
  const renderItems = (items: NavItem[]) => (
    <ul className="space-y-1">
      {items.map(({ to, labelKey, icon: Icon, end }) => (
        <li key={to}>
          <RailTooltip label={t(labelKey)} enabled={rail}>
            <SidebarLink to={to} end={end} rail={rail} onNavigate={onCloseMobile}>
              <Icon className="size-[1.15rem] shrink-0" aria-hidden="true" />
              <span className={cn(rail && "md:sr-only")}>{t(labelKey)}</span>
            </SidebarLink>
          </RailTooltip>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-ink/40 md:hidden" aria-hidden="true" onClick={onCloseMobile} />
      )}
      <aside
        id="app-sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-sidebar flex-col border-r border-line bg-surface",
          "transition-[width,translate] duration-200 ease-out",
          collapsed && "md:w-rail",
          mobileOpen ? "translate-x-0 shadow-pop" : "-translate-x-full md:translate-x-0",
        )}
      >
        <div
          className={cn("flex h-16 shrink-0 items-center gap-3 px-5", rail && "md:justify-center md:px-0")}
        >
          <VaultMark />
          <span className={cn("text-[1.05rem] font-semibold tracking-tight", rail && "md:sr-only")}>
            {t("app.name")}
          </span>
          <IconButton label={t("nav.closeMenu")} onClick={onCloseMobile} className="ml-auto md:hidden">
            <X className="size-5" aria-hidden="true" />
          </IconButton>
        </div>

        <nav aria-label={t("nav.main")} className="flex-1 overflow-y-auto px-3 py-4">
          {renderItems(NAV_ITEMS.filter((item) => can(item.permission)))}

          {can("archive.browse") && (
            <div className="mt-7">
              {rail ? (
                <>
                  <h2 className="px-3 pb-2 text-xs font-medium text-ink-subtle md:sr-only">
                    {t("nav.queues")}
                  </h2>
                  <div className="mx-3 border-t border-line" aria-hidden="true" />
                </>
              ) : (
                <>
                  <h2>
                    <button
                      type="button"
                      onClick={toggleQueues}
                      aria-expanded={queuesOpen}
                      aria-controls="sidebar-queues"
                      className="flex w-full items-center gap-1 rounded-control px-3 pb-2 text-left text-xs font-medium text-ink-subtle hover:text-ink"
                    >
                      <ChevronRight
                        className={cn("size-3.5 transition-transform", queuesOpen && "rotate-90")}
                        aria-hidden="true"
                      />
                      {t("nav.queues")}
                    </button>
                  </h2>
                  {queuesOpen && (
                    <div id="sidebar-queues">
                      <QueueTree onNavigate={onCloseMobile} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {adminItems.length > 0 && (
            <div className="mt-7">
              <h2 className={cn("px-3 pb-2 text-xs font-medium text-ink-subtle", rail && "md:sr-only")}>
                {t("nav.administration")}
              </h2>
              {renderItems(adminItems)}
            </div>
          )}
        </nav>

        <div
          className={cn(
            "flex shrink-0 items-center gap-2 border-t border-line px-4 py-3",
            rail && "md:flex-col md:px-0",
          )}
        >
          <StudioSignature compact={rail} className="min-w-0 flex-1" />
          <IconButton
            label={collapsed ? t("nav.expand") : t("nav.collapse")}
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls="app-sidebar"
            className="hidden md:inline-flex"
          >
            {collapsed ? (
              <PanelLeftOpen className="size-[1.15rem]" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="size-[1.15rem]" aria-hidden="true" />
            )}
          </IconButton>
        </div>
      </aside>
    </>
  );
}
